import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HelpCircle, Play, RefreshCw, BookOpen, ChevronDown, CheckCircle, X } from 'lucide-react';
import { useTour } from './TourProvider';

const TOUR_INFO = {
  welcome: { 
    name: 'Welcome Tour', 
    description: 'Complete app walkthrough',
    icon: '👋',
    duration: '2 min'
  },
  dashboard: { 
    name: 'Dashboard Tour', 
    description: 'Understanding your metrics',
    icon: '📊',
    duration: '1 min'
  },
  controls: { 
    name: 'Controls Tour', 
    description: 'Managing compliance controls',
    icon: '✅',
    duration: '1 min'
  },
  evidence: { 
    name: 'Evidence Tour', 
    description: 'Uploading & managing evidence',
    icon: '📎',
    duration: '1 min'
  },
  soc2: { 
    name: 'SOC 2 Tour', 
    description: 'Preparing for SOC 2 audit',
    icon: '🎯',
    duration: '1 min'
  },
  vulnerabilities: { 
    name: 'Vulnerabilities Tour', 
    description: 'Tracking security issues',
    icon: '🛡️',
    duration: '1 min'
  },
  risks: { 
    name: 'Risk Register Tour', 
    description: 'Managing organizational risks',
    icon: '⚠️',
    duration: '1 min'
  },
  threatIntel: { 
    name: 'Threat Intel Tour', 
    description: 'Understanding threat feeds',
    icon: '🌐',
    duration: '1 min'
  },
};

export function TourButton() {
  const { startTour, resetTours, completedTours, isFirstVisit, dismissFirstVisit } = useTour();
  const [isOpen, setIsOpen] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const dropdownRef = useRef(null);

  // Show welcome modal only for genuine first visits
  useEffect(() => {
    if (isFirstVisit) {
      setShowWelcomeModal(true);
    }
  }, [isFirstVisit]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const dismissWelcomeModal = useCallback(() => {
    setShowWelcomeModal(false);
    dismissFirstVisit();
  }, [dismissFirstVisit]);

  const handleStartTour = (tourId) => {
    setIsOpen(false);
    dismissWelcomeModal();
    startTour(tourId);
  };

  const handleResetTours = () => {
    resetTours();
    setIsOpen(false);
  };

  const completedCount = completedTours.length;
  const totalTours = Object.keys(TOUR_INFO).length;

  return (
    <>
      {/* Welcome Modal for First-Time Users */}
      {showWelcomeModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden animate-slide-up">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-6 text-white">
              <button 
                onClick={dismissWelcomeModal}
                className="absolute top-4 right-4 text-white/60 hover:text-white"
              >
                <X size={20} />
              </button>
              <div className="text-4xl mb-3">👋</div>
              <h2 className="text-2xl font-bold">Welcome to Zentry!</h2>
              <p className="text-slate-300 mt-2">
                Your compliance journey starts here. Let us show you around.
              </p>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-sm flex-shrink-0">1</div>
                  <div>
                    <h4 className="font-medium text-slate-900">Take the Tour</h4>
                    <p className="text-sm text-slate-500">Learn the key features in 2 minutes</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-sm flex-shrink-0">2</div>
                  <div>
                    <h4 className="font-medium text-slate-900">Select Frameworks</h4>
                    <p className="text-sm text-slate-500">Choose SOC 2, ISO 27001, HIPAA, etc.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-sm flex-shrink-0">3</div>
                  <div>
                    <h4 className="font-medium text-slate-900">Start Complying</h4>
                    <p className="text-sm text-slate-500">Update controls and upload evidence</p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => handleStartTour('welcome')}
                  className="flex-1 bg-slate-900 text-white py-3 px-4 rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
                >
                  <Play size={18} />
                  Start Tour
                </button>
                <button
                  onClick={dismissWelcomeModal}
                  data-testid="tour-skip-btn"
                  className="px-4 py-3 text-slate-600 hover:text-slate-900 font-medium"
                >
                  Skip
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tour Button & Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm"
          aria-label="Help and Tours"
        >
          <HelpCircle size={18} />
          <span className="text-sm font-medium hidden sm:inline">Help</span>
          <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden animate-fade-in">
            {/* Header */}
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BookOpen size={16} className="text-slate-500" />
                  <span className="font-semibold text-slate-900 text-sm">Interactive Tours</span>
                </div>
                <span className="text-xs text-slate-500">{completedCount}/{totalTours} completed</span>
              </div>
            </div>

            {/* Tour List */}
            <div className="max-h-80 overflow-y-auto">
              {Object.entries(TOUR_INFO).map(([tourId, info]) => {
                const isCompleted = completedTours.includes(tourId);
                return (
                  <button
                    key={tourId}
                    onClick={() => handleStartTour(tourId)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left border-b border-slate-100 last:border-0"
                  >
                    <span className="text-xl flex-shrink-0">{info.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 text-sm">{info.name}</span>
                        {isCompleted && (
                          <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate">{info.description}</p>
                    </div>
                    <span className="text-xs text-slate-400 flex-shrink-0">{info.duration}</span>
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="bg-slate-50 px-4 py-3 border-t border-slate-200">
              <button
                onClick={handleResetTours}
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                <RefreshCw size={14} />
                Reset all tours
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default TourButton;
